import { useCallback, useMemo } from "react";
import { useEntitiesContexts } from "@hooks/useEntitiesContexts";

export const useSendingEmailEntityMapping = () => {
    const entitiesContexts = useEntitiesContexts();
    // `useEntitiesContexts` returns `undefined` until the workspace entities
    // have loaded (and an array afterwards). Callers must not map entity
    // uuids ⇆ ids until this is `true`, otherwise a not-yet-loaded list looks
    // identical to "no entities" and silently drops existing assignments.
    const isReady = entitiesContexts !== undefined;
    const entities = useMemo(
        () => entitiesContexts?.map((context) => context.entity) ?? [],
        [entitiesContexts],
    );

    const entitiesById = useMemo(
        () => new Map(entities.map((entity) => [entity.id, entity])),
        [entities],
    );
    const entitiesByUuid = useMemo(
        () => new Map(entities.map((entity) => [entity.uuid, entity])),
        [entities],
    );

    const entityIdsToUuids = useCallback(
        (entityIds: number[]) =>
            entityIds
                .map((entityId) => entitiesById.get(entityId)?.uuid)
                .filter((uuid): uuid is string => Boolean(uuid)),
        [entitiesById],
    );

    const entityUuidsToIds = useCallback(
        (entityUuids: string[]) =>
            entityUuids
                .map((uuid) => entitiesByUuid.get(uuid)?.id)
                .filter((id): id is number => id !== undefined),
        [entitiesByUuid],
    );

    return { entityIdsToUuids, entityUuidsToIds, isReady };
};
